
import React, { useState } from 'react';
import { useHOManager } from './hooks/useHOManager';

export const MarginFixation: React.FC = () => {
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [margins, setMargins] = useState({
    making_charge_margin: 0,
    stone_margin: 0,
    overall_margin: 0
  });

  const { pendingProducts, stoneRates, updateMargins, activateProduct } = useHOManager();

  const handleProductSelect = (product: any) => {
    setSelectedProduct(product);
    setMargins({
      making_charge_margin: 0,
      stone_margin: 0,
      overall_margin: 0
    });
  };

  const handleMarginUpdate = async () => {
    if (!selectedProduct) return;

    try {
      await updateMargins.mutateAsync({
        product_id: selectedProduct.id,
        ...margins
      });

      await activateProduct.mutateAsync(selectedProduct.id);
      
      alert('Margins set and product activated successfully!');
      setSelectedProduct(null);
      setMargins({
        making_charge_margin: 0,
        stone_margin: 0,
        overall_margin: 0
      });
    } catch (error) {
      console.error('Failed to update margins:', error);
    }
  };

  // Show loading state while data is being fetched
  if (pendingProducts.isLoading || stoneRates.isLoading) {
    return <div className="p-6">Loading...</div>;
  }

  // Get the actual data arrays with fallback to empty arrays
  const pendingProductsData = pendingProducts.data || [];
  const stoneRatesData = stoneRates.data || [];

  return (
    <div className="p-6 space-y-6">
      <h1 className="text-2xl font-semibold text-primary">Margin Fixation</h1>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pending Products */}
        <div className="bg-white border rounded-lg p-6">
          <h2 className="text-lg font-semibold mb-4">Pending Products</h2>
          
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {pendingProductsData.length === 0 ? (
              <div className="text-gray-500 text-center py-8">
                No pending products found
              </div>
            ) : (
              pendingProductsData.map((product) => (
                <div
                  key={product.id}
                  className={`border rounded-lg p-4 cursor-pointer transition-all ${
                    selectedProduct?.id === product.id ? 'border-primary bg-primary/5' : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => handleProductSelect(product)}
                >
                  <div className="space-y-2">
                    <h3 className="font-semibold">{product.model_name}</h3>
                    <p className="text-sm text-gray-600">{product.description}</p>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>Metal: {product.metal_type}</div>
                      <div>Weight: {product.gross_weight}g</div>
                      <div>Making: ₹{product.making_charges}</div>
                      <div>Delivery: {product.delivery_days} days</div>
                    </div>
                    <div className="text-xs text-gray-500">
                      Vendor: {product.vendors?.name}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Margin Setting */}
        {selectedProduct && (
          <div className="bg-white border rounded-lg p-6">
            <h2 className="text-lg font-semibold mb-4">Set Margins for {selectedProduct.model_name}</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Making Charge Margin (%)</label>
                <input
                  type="number"
                  step="0.1"
                  value={margins.making_charge_margin}
                  onChange={(e) => setMargins(prev => ({ 
                    ...prev, 
                    making_charge_margin: parseFloat(e.target.value) 
                  }))}
                  className="w-full border rounded px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Stone Margin (%)</label>
                <input
                  type="number"
                  step="0.1"
                  value={margins.stone_margin}
                  onChange={(e) => setMargins(prev => ({ 
                    ...prev, 
                    stone_margin: parseFloat(e.target.value) 
                  }))}
                  className="w-full border rounded px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Overall Margin (%)</label>
                <input
                  type="number"
                  step="0.1"
                  value={margins.overall_margin}
                  onChange={(e) => setMargins(prev => ({ 
                    ...prev, 
                    overall_margin: parseFloat(e.target.value) 
                  }))}
                  className="w-full border rounded px-3 py-2"
                />
              </div>

              {/* Product Details */}
              <div className="border-t pt-4 space-y-2">
                <h3 className="font-medium">Product Details:</h3>
                <div className="text-sm space-y-1">
                  <div>Base Making Charges: ₹{selectedProduct.making_charges}</div>
                  <div>Value Addition: ₹{selectedProduct.value_addition}</div>
                  <div>Gross Weight: {selectedProduct.gross_weight}g</div>
                  {selectedProduct.stone_details?.length > 0 && (
                    <div>
                      Stones: {selectedProduct.stone_details.map((stone: any) => 
                        `${stone.stone_type} (${stone.is_precious ? 'Precious' : 'Semi-precious'})`
                      ).join(', ')}
                    </div>
                  )}
                </div>
              </div>

              <button
                onClick={handleMarginUpdate}
                className="w-full bg-primary text-white py-2 rounded hover:bg-primary/90 transition-colors"
              >
                Set Margins & Activate Product
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Stone Rates Management */}
      <div className="bg-white border rounded-lg p-6">
        <h2 className="text-lg font-semibold mb-4">Stone Rates</h2>
        
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b">
                <th className="text-left p-2">Stone Type</th>
                <th className="text-left p-2">Quality</th>
                <th className="text-left p-2">Size Range</th>
                <th className="text-left p-2">Type</th>
                <th className="text-left p-2">Rate/Carat</th>
                <th className="text-left p-2">Rate/Cent</th>
              </tr>
            </thead>
            <tbody>
              {stoneRatesData.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-4 text-gray-500">
                    No stone rates found
                  </td>
                </tr>
              ) : (
                stoneRatesData.map((rate) => (
                  <tr key={rate.id} className="border-b">
                    <td className="p-2">{rate.stone_type}</td>
                    <td className="p-2">{rate.quality_grade}</td>
                    <td className="p-2">{rate.size_range}</td>
                    <td className="p-2">
                      <span className={`px-2 py-1 rounded text-xs ${
                        rate.is_precious ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {rate.is_precious ? 'Precious' : 'Semi-precious'}
                      </span>
                    </td>
                    <td className="p-2">₹{rate.rate_per_carat}</td>
                    <td className="p-2">₹{rate.rate_per_cent}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default MarginFixation;
